import React, { useState, useEffect } from 'react';
import BlogPostCard from '../components/BlogPage/BlogPostCard';
import BlogService from '../services/BlogService';
import CallToActionSection from '../../core/components/Common/CallToActionSection';
import '../styles/BlogPage.css';
import { PrimaryButton } from '../../ui/components/buttons';

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All Articles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadArticlesByCategory();
  }, [selectedCategory]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [articlesResponse, categoriesResponse] = await Promise.all([
        BlogService.getAllArticles(),
        BlogService.getCategories()
      ]);

      if (articlesResponse.success) {
        setArticles(articlesResponse.data);
      }
      
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }
    } catch (err) {
      setError('Failed to load blog content');
      console.error('Error loading blog data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadArticlesByCategory = async () => {
    try {
      setLoading(true);
      const response = await BlogService.getArticlesByCategory(selectedCategory);
      
      if (response.success) {
        setArticles(response.data);
      } else {
        setError('Failed to load articles');
      }
    } catch (err) {
      setError('Failed to load articles');
      console.error('Error loading articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  if (loading && articles.length === 0) {
    return (
      <div className="blog-page">
        <div className="container">
          <div className="blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading articles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-page">
        <div className="container">
          <div className="blog-error">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <PrimaryButton onClick={loadInitialData}>
              Try Again
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">
              Latest News on <span className="highlight">Video Production</span> & Creative Content
            </h1>
            <p className="blog-hero-subtitle">
              Welcome to our Blog, your go-to source for video production insights, creative direction, and real-time content strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="blog-categories">
        <div className="container">
          <div className="blog-category-filters">
            {categories.map((category) => (
              <button
                key={category}
                className={`blog-category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="blog-articles">
        <div className="container">
          {loading ? (
            <div className="blog-loading">
              <div className="loading-spinner"></div>
              <p>Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="blog-empty">
              <h3>No articles found</h3>
              <p>Try selecting a different category or check back later for new content.</p>
            </div>
          ) : (
            <div className="blog-articles-grid">
              {articles.map((article) => (
                <BlogPostCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action Section */}
      <CallToActionSection 
        title="Ready to Create Impactful Content?"
        subtitle="Get expert guidance and personalized solutions for your video production projects"
        buttonText="Start Your Project"
      />
    </div>
  );
};

export default BlogPage;
